import React, { useCallback, type FC, useMemo, useState } from 'react';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import {
  useCreateServiceProviderStripeAccount,
  useServiceProviderStripeAccount,
} from '@pflegenavi/service-provider-frontend/api';
import type { StripeAccountInfo } from '@pflegenavi/shared/api';
import {
  Button,
  Label,
  LoadingContainer,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { Card, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const ServiceProviderStripe: FC = () => {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to extract the globalServiceProviderId
  const { getTokenImmediate } = useAuthentication();
  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: stripeAccount, isLoading } = useServiceProviderStripeAccount({
    globalServiceProviderId,
  });
  const { mutateAsync: createStripeAccount } =
    useCreateServiceProviderStripeAccount();

  const [creationLoading, setCreationLoading] = useState(false);

  const startOnboarding = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const handleAccountCreation = useCallback(async () => {
    if (globalServiceProviderId) {
      setCreationLoading(true);

      try {
        const account = await createStripeAccount({ globalServiceProviderId });
        startOnboarding(account.url);
        // We don't set the creation loading to false here, because the user will be redirected to Stripe and
        // otherwise the account invalid box might flicker up
      } catch (error) {
        enqueueErrorSnackbar(error, t('errors.something-went-wrong'));
        setCreationLoading(false);
      }
    }
  }, [
    createStripeAccount,
    enqueueErrorSnackbar,
    globalServiceProviderId,
    startOnboarding,
    t,
  ]);

  if (isLoading || creationLoading) {
    return <LoadingContainer />;
  }

  return (
    <ServiceProviderStripeCard
      stripeAccount={stripeAccount}
      startOnboarding={startOnboarding}
      handleAccountCreation={handleAccountCreation}
    />
  );
};

interface ServiceProviderStripeCardProps {
  stripeAccount: StripeAccountInfo | { stripeAccountId: undefined } | undefined;
  startOnboarding: (url: string) => void;
  handleAccountCreation: () => void;
}

export const ServiceProviderStripeCard: FC<ServiceProviderStripeCardProps> = ({
  stripeAccount,
  handleAccountCreation,
  startOnboarding,
}) => {
  const theme = useTheme();

  const stripeActive =
    stripeAccount?.stripeAccountId &&
    stripeAccount.accountOnboardingStatus === 'complete'
      ? stripeAccount
      : undefined;

  const stripePending =
    stripeAccount?.stripeAccountId &&
    stripeAccount.accountOnboardingStatus === 'pending'
      ? stripeAccount
      : undefined;

  const cardContent = useMemo(() => {
    if (stripeActive) {
      return <ActiveStripeCardContent />;
    } else if (stripePending) {
      return (
        <InactiveErrorStripeCardContent
          onClick={() => startOnboarding(stripePending.url)}
        />
      );
    }
    return <InactiveStripeCardContent onClick={handleAccountCreation} />;
  }, [handleAccountCreation, startOnboarding, stripeActive, stripePending]);

  return (
    <Card
      sx={{
        p: 3,
        backgroundColor: !stripeActive ? theme.palette.primary.lighter : '',
      }}
    >
      {cardContent}
    </Card>
  );
};

const ActiveStripeCardContent = () => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          mr: 1,
          fontWeight: 'bold',
        }}
      >
        {t('service-provider-app.stripe.stripe-status')}
      </Typography>
      <Label
        variant="filled"
        color="success"
        sx={{
          color: 'white',
        }}
      >
        {t('service-provider-app.stripe.status.active')}
      </Label>
    </Stack>
  );
};

const InactiveStripeCardContent = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="column"
      spacing={1.5}
      sx={{
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            mr: 1,
            fontWeight: 'bold',
          }}
        >
          {t('service-provider-app.stripe.stripe-status')}
        </Typography>
        <Label
          variant="filled"
          color="error"
          sx={{
            color: 'white',
          }}
        >
          {t('service-provider-app.stripe.status.inactive')}
        </Label>
      </Stack>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          alignItems: 'flex-end',
        }}
      >
        <Typography>
          {t('service-provider-app.stripe.initiate-stripe-setup')}
        </Typography>
        <Button
          disabled={false}
          variant="contained"
          color="primary"
          size="medium"
          onClick={onClick}
        >
          {t('service-provider-app.stripe.go-to-stripe')}
        </Button>
      </Stack>
    </Stack>
  );
};

const InactiveErrorStripeCardContent = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="column"
      spacing={1.5}
      sx={{
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            mr: 1,
            fontWeight: 'bold',
          }}
        >
          {t('service-provider-app.stripe.stripe-status')}
        </Typography>
        <Label
          variant="filled"
          color="error"
          sx={{
            color: 'white',
          }}
        >
          {t('service-provider-app.stripe.status.inactive')}
        </Label>
      </Stack>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          alignItems: 'flex-end',
        }}
      >
        <Typography>
          {t('service-provider-app.stripe.stripe-requires-info-error')}
        </Typography>
        <Button
          disabled={false}
          variant="contained"
          color="primary"
          size="medium"
          onClick={onClick}
        >
          {t('service-provider-app.stripe.go-to-stripe')}
        </Button>
      </Stack>
    </Stack>
  );
};
